/**
 * Cart Drawer Contextual Recommendations
 *
 * Hydrates the cart drawer "You may also like" rail from the same resilient
 * recommendation stack as the storefront rail: contextual/cart first, then
 * personalized-home, demand, newest, and catalog fallbacks.
 */

(function () {
  'use strict';

  if (window.__mmCartRecsInit) return;
  window.__mmCartRecsInit = true;

  var DRAWER_SELECTOR = '[data-mini-cart-drawer]';
  var RAIL_SELECTOR = '[data-mm-cart-recs]';
  var RAIL_TRACK_SELECTOR = '[data-mm-cart-recs-rail]';
  var CART_INTENT_SELECTORS = ['[data-onboarding-target="cart-drawer-trigger"]', '[data-mobile-header-cart-btn]', '.site-header-cart--button'];
  var RECOMMENDATIONS_ENDPOINT = '/apps/garden/v1/recommendations';
  var TOP_PRODUCTS_ENDPOINT = '/apps/garden/v1/demand/top-products?availability=available';
  var NEWEST_PRODUCTS_ENDPOINT = '/collections/all/products.json?sort_by=created-descending';
  var CATALOG_PRODUCTS_ENDPOINT = '/collections/all/products.json';
  var DISPLAY_LIMIT = 4;
  var POOL_LIMIT = 20;
  var CACHE_TTL_MS = 60 * 60 * 1000;
  var CACHE_KEY_PREFIX = 'mrmaple:cart-recommendations:v5';
  var SKELETON_COUNT = 2;
  var PREWARM_DELAY_MS = 24;
  var HYDRATE_DELAY_MS = 50;
  var CART_MUTATION_DEBOUNCE_MS = 160;
  var CATALOG_RANDOM_PAGE_MIN = 2;
  var CATALOG_RANDOM_PAGE_MAX = 24;
  var CATALOG_RANDOM_PAGE_SIZE = 50;
  var CATALOG_RANDOM_ATTEMPTS = 3;

  var currentEntry = null;
  var lifecycleToken = 0;
  var inflightKey = null;
  var inflightPromise = null;
  var prewarmTimer = 0;
  var hydrateTimer = 0;
  var cartMutationTimer = 0;
  var lastCartSignature = '';

  function getDrawer() {
    return document.querySelector(DRAWER_SELECTOR);
  }

  function getRail() {
    return document.querySelector(RAIL_SELECTOR);
  }

  function getRailTrack() {
    return document.querySelector(RAIL_TRACK_SELECTOR);
  }

  function isDrawerOpen(drawer) {
    return !!drawer && drawer.getAttribute('aria-hidden') === 'false';
  }

  function hasCartItems(drawer) {
    if (!drawer) return false;

    var itemsContainer = drawer.querySelector('[data-mini-cart-items]');
    if (!itemsContainer || itemsContainer.classList.contains('hidden')) {
      return false;
    }

    return !!itemsContainer.querySelector('[data-mini-cart-item]');
  }

  function getCartItemElements(drawer) {
    if (!drawer) return [];
    var itemsContainer = drawer.querySelector('[data-mini-cart-items]');
    if (!itemsContainer || itemsContainer.classList.contains('hidden')) {
      return [];
    }
    return Array.prototype.slice.call(itemsContainer.querySelectorAll('[data-mini-cart-item]'));
  }

  function normalizeCartValue(value) {
    return String(value || '').trim();
  }

  function lastGidSegment(value) {
    var normalized = normalizeCartValue(value);
    if (!normalized) return '';
    var index = normalized.lastIndexOf('/');
    return index >= 0 ? normalized.slice(index + 1) : normalized;
  }

  function uniqueSorted(values) {
    var seen = Object.create(null);
    var output = [];

    for (var i = 0; i < values.length; i += 1) {
      var value = normalizeCartValue(values[i]);
      if (!value || seen[value]) continue;
      seen[value] = true;
      output.push(value);
    }

    return output.sort();
  }

  function getCartProductIds(drawer) {
    return uniqueSorted(
      getCartItemElements(drawer).map(function (item) {
        return item.getAttribute('data-product-id') || item.getAttribute('data-product-gid') || '';
      }),
    );
  }

  function getCartProductHandles(drawer) {
    return uniqueSorted(
      getCartItemElements(drawer).map(function (item) {
        var handle = item.getAttribute('data-product-handle') || '';
        if (handle) return handle;

        var link = item.querySelector('a[href*="/products/"]');
        var match = link && String(link.getAttribute('href') || '').match(/\/products\/([^/?#]+)/i);
        if (!match) return '';
        try {
          return decodeURIComponent(match[1]);
        } catch (_) {
          return match[1];
        }
      }),
    );
  }

  function getCartVariantIds(drawer) {
    return uniqueSorted(
      getCartItemElements(drawer).map(function (item) {
        return item.getAttribute('data-variant-id') || '';
      }),
    );
  }

  function getCartSignature(drawer) {
    var productIds = getCartProductIds(drawer);
    if (productIds.length) return 'products:' + productIds.join(',');

    var handles = getCartProductHandles(drawer);
    if (handles.length) return 'handles:' + handles.join(',');

    var variantIds = getCartVariantIds(drawer);
    if (variantIds.length) return 'variants:' + variantIds.join(',');

    return 'empty';
  }

  function getCartContext(drawer) {
    return {
      productIds: getCartProductIds(drawer),
      handles: getCartProductHandles(drawer),
      signature: getCartSignature(drawer),
    };
  }

  function setRailVisibility(rail, visible) {
    if (!rail) return;

    if (visible) {
      rail.removeAttribute('hidden');
    } else {
      rail.setAttribute('hidden', '');
    }
  }

  function buildSkeleton(count) {
    var html = '';
    for (var i = 0; i < count; i += 1) {
      html +=
        '<div class="mm-cart-rec-card neu-card neu-card--skeleton" aria-hidden="true">' +
        '<div class="neu-card__image-wrap"></div>' +
        '<div class="neu-card__info">' +
        '<div class="neu-card__title">&nbsp;</div>' +
        '<div class="neu-card__price">&nbsp;</div>' +
        '</div>' +
        '</div>';
    }
    return html;
  }

  function getCustomerKey() {
    if (window.ShopifyCustomer && window.ShopifyCustomer.id) {
      return String(window.ShopifyCustomer.id);
    }

    if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.customerId) {
      return String(window.ShopifyAnalytics.meta.customerId);
    }

    return 'anon';
  }

  function getCustomerZone() {
    if (window.MMCustomerZone) {
      return String(window.MMCustomerZone).trim().toLowerCase();
    }

    try {
      var stored = localStorage.getItem('usdaZone') || localStorage.getItem('mrmaple_zone') || localStorage.getItem('mrmaple:customer:zone') || '';
      return String(stored || '')
        .trim()
        .toLowerCase();
    } catch (_) {
      return '';
    }
  }

  function getCacheKey() {
    var drawer = getDrawer();
    var cartSignature = getCartSignature(drawer);

    return [CACHE_KEY_PREFIX, getCustomerKey(), getCustomerZone() || 'no-zone', cartSignature || 'empty', DISPLAY_LIMIT, POOL_LIMIT].join(':');
  }

  function readPersistentCache() {
    var cacheKey = getCacheKey();

    if (currentEntry && currentEntry.cacheKey === cacheKey) {
      return currentEntry;
    }

    try {
      var raw = localStorage.getItem(cacheKey);
      if (!raw) return null;

      var parsed = JSON.parse(raw);
      if (!parsed || parsed.cacheKey !== cacheKey) {
        return null;
      }

      currentEntry = parsed;
      return parsed;
    } catch (_) {
      try {
        localStorage.removeItem(cacheKey);
      } catch (_) {}
      return null;
    }
  }

  function clearPersistentCache() {
    var cacheKey = getCacheKey();
    currentEntry = null;

    try {
      localStorage.removeItem(cacheKey);
    } catch (_) {}
  }

  function writePersistentCache(pool) {
    var normalizedPool = Array.isArray(pool) ? pool : [];
    var now = Date.now();
    var entry = {
      cacheKey: getCacheKey(),
      fetchedAt: now,
      expiresAt: now + CACHE_TTL_MS,
      cartSignature: getCartSignature(getDrawer()),
      pool: normalizedPool,
    };

    currentEntry = entry;

    if (normalizedPool.length > 0) {
      try {
        localStorage.setItem(entry.cacheKey, JSON.stringify(entry));
      } catch (_) {}
    }

    return entry;
  }

  function getCachedEntry(options) {
    var allowExpired = options && options.allowExpired === true;
    var entry = readPersistentCache();
    if (!entry) return null;

    if (typeof entry.expiresAt !== 'number' || entry.expiresAt <= Date.now()) {
      if (!allowExpired) {
        return null;
      }
      entry.isStale = true;
    } else {
      entry.isStale = false;
    }

    if (!Array.isArray(entry.pool)) {
      clearPersistentCache();
      return null;
    }

    return entry;
  }

  function isValidProductHandle(handle) {
    var normalized = String(handle || '').trim();
    return !!normalized && normalized !== 'null' && normalized !== 'undefined';
  }

  function normalizeProductUrl(url, handle) {
    var normalized = String(url || '').trim();
    if (normalized) return normalized;
    return handle ? '/products/' + encodeURIComponent(handle) : '';
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function cleanTitle(value) {
    return String(value || 'Recommended plant')
      .replace(/^(?:FOR\s+)?PICK\s*UP\s+ONLY\s*\|\s*/i, '')
      .replace(/\s*\|\s*DOES NOT SHIP\s*$/i, '')
      .replace(/DOES NOT SHIP\s*$/i, '')
      .trim();
  }

  function normalizeImageUrl(value) {
    if (!value) return '';
    var rawValue = typeof value === 'string' ? value : typeof value === 'object' && value && typeof value.url === 'string' ? value.url : '';
    var normalized = rawValue.trim();
    if (!normalized) return '';
    if (normalized.indexOf('//') === 0) return 'https:' + normalized;
    if (normalized.charAt(0) === '/') return normalized;
    if (/^https?:\/\//i.test(normalized)) return normalized;
    return '';
  }

  function formatAmountPrice(amount, currencyCode) {
    var numericAmount = Number(amount);
    var normalizedCurrency = String(currencyCode || '').trim() || 'USD';
    if (!Number.isFinite(numericAmount)) return '';

    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: normalizedCurrency,
      }).format(numericAmount);
    } catch (_) {
      return normalizedCurrency + ' ' + numericAmount.toFixed(2);
    }
  }

  function formatApiPrice(price, currency) {
    if (price && typeof price === 'object') {
      return formatAmountPrice(price.amount, price.currencyCode);
    }
    if (price == null || price === '') return '';
    return formatAmountPrice(price, currency);
  }

  function formatMoneyFromCents(cents) {
    var safeCents = Number.isFinite(Number(cents)) ? Number(cents) : null;
    if (safeCents == null) return '';

    var moneyFormat =
      window.SHOP_MONEY_FORMAT ||
      (window.Theme && window.Theme.moneyFormat) ||
      (window.theme && window.theme.moneyFormat) ||
      undefined;

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try {
        return window.Shopify.formatMoney(safeCents, moneyFormat);
      } catch (_) {}
    }

    var currencyCode =
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
      document.documentElement.getAttribute('data-currency') ||
      'USD';

    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode,
      }).format(safeCents / 100);
    } catch (_) {
      return '$' + (safeCents / 100).toFixed(2);
    }
  }

  function formatScalarPrice(amount, currencyCode, unit) {
    if (amount == null || amount === '') return '';

    var rawString = typeof amount === 'string' ? amount.trim() : '';
    var numericAmount = Number(rawString || amount);
    if (!Number.isFinite(numericAmount)) return '';

    if (unit === 'cents') {
      return formatMoneyFromCents(numericAmount);
    }

    return formatAmountPrice(numericAmount, currencyCode);
  }

  function normalizeAvailability(item) {
    if (!item || typeof item !== 'object') return null;
    if (typeof item.available === 'boolean') {
      return item.available;
    }
    if (typeof item.availableForSale === 'boolean') {
      return item.availableForSale;
    }
    if (Array.isArray(item.variants) && item.variants.length > 0) {
      var sawKnownAvailability = false;
      for (var index = 0; index < item.variants.length; index += 1) {
        var variant = item.variants[index];
        if (!variant || typeof variant !== 'object') continue;
        if (typeof variant.available === 'boolean') {
          sawKnownAvailability = true;
          if (variant.available) return true;
        }
        if (typeof variant.availableForSale === 'boolean') {
          sawKnownAvailability = true;
          if (variant.availableForSale) return true;
        }
      }
      if (sawKnownAvailability) return false;
    }
    return null;
  }

  function normalizeProductImage(item) {
    if (!item || typeof item !== 'object') return '';
    if (item.image) return normalizeImageUrl(item.image);
    if (item.featured_image) return normalizeImageUrl(item.featured_image);
    if (item.featuredImage && item.featuredImage.url) return normalizeImageUrl(item.featuredImage.url);
    if (Array.isArray(item.images) && item.images.length > 0) return normalizeImageUrl(item.images[0]);
    return '';
  }

  function normalizePriceText(item, source) {
    if (!item || typeof item !== 'object') return '';

    var explicitPriceText = String(item.priceText || item.price_text || '').trim();
    if (explicitPriceText) return explicitPriceText;

    if (item.price && typeof item.price === 'object') {
      return formatApiPrice(item.price, item.currency || item.currencyCode);
    }

    var isShopifyCatalogSource = source === 'newest' || source === 'catalog-random' || Array.isArray(item.variants);
    return formatScalarPrice(item.price, item.currency || item.currencyCode, isShopifyCatalogSource ? 'cents' : 'amount');
  }

  function normalizeRecommendedHandle(item) {
    if (!item || typeof item !== 'object') return '';

    if (item.handle) {
      var handle = String(item.handle).trim();
      return isValidProductHandle(handle) ? handle : '';
    }

    var rawUrl = String(item.url || item.productUrl || item.href || '').trim();
    if (!rawUrl) return '';

    var match = rawUrl.match(/\/products\/([^/?#]+)/i);
    if (!match) return '';

    try {
      var decoded = decodeURIComponent(match[1]).trim();
      return isValidProductHandle(decoded) ? decoded : '';
    } catch (_) {
      var fallback = String(match[1]).trim();
      return isValidProductHandle(fallback) ? fallback : '';
    }
  }

  function normalizeRecommendationItem(item, source) {
    if (!item || typeof item !== 'object') return null;

    var handle = normalizeRecommendedHandle(item);
    var productId = String(item.productId || item.productGid || item.id || '').trim();
    var available = normalizeAvailability(item);
    if (!handle) return null;
    if (available === false) return null;

    return {
      source: source,
      handle: handle,
      productId: productId,
      title: cleanTitle(item.title || ''),
      image: normalizeProductImage(item),
      priceText: normalizePriceText(item, source),
      vendor: String(item.vendor || '').trim(),
      reason: String(item.reason || '').trim(),
      reasonSource: String(item.reasonSource || '').trim(),
      url: normalizeProductUrl(item.url, handle),
      available: available !== false,
      score: typeof item.score === 'number' ? item.score : null,
    };
  }

  function productMatchesCart(product, cartContext) {
    if (!product || !cartContext) return false;

    var handle = String(product.handle || '').trim();
    if (handle && cartContext.handles.indexOf(handle) !== -1) {
      return true;
    }

    var productId = String(product.productId || '').trim();
    if (!productId) return false;

    var productIdSegment = lastGidSegment(productId);
    return cartContext.productIds.some(function (cartProductId) {
      return cartProductId === productId || lastGidSegment(cartProductId) === productIdSegment;
    });
  }

  function dedupeProducts(products, cartContext) {
    var seen = Object.create(null);
    var output = [];

    for (var i = 0; i < (products || []).length; i += 1) {
      var product = products[i];
      if (!product || !isValidProductHandle(product.handle)) continue;
      if (product.available === false) continue;
      if (productMatchesCart(product, cartContext)) continue;
      if (seen[product.handle]) continue;
      seen[product.handle] = true;
      output.push(product);
    }

    return output;
  }

  async function fetchRecommendedPool() {
    var drawer = getDrawer();
    var cartContext = getCartContext(drawer);
    if (!cartContext.productIds.length) {
      return [];
    }

    var pool = [];

    function merge(items) {
      pool = dedupeProducts(pool.concat(items || []), cartContext).slice(0, POOL_LIMIT);
    }

    merge(await fetchContextualCartProducts(cartContext.productIds, POOL_LIMIT));

    if (pool.length < DISPLAY_LIMIT) {
      merge(await fetchPersonalizedProducts(POOL_LIMIT));
    }

    if (pool.length < DISPLAY_LIMIT) {
      var fallbacks = await Promise.all([fetchDemandProducts(POOL_LIMIT), fetchNewestProducts(POOL_LIMIT)]);
      merge(fallbacks[0]);
      merge(fallbacks[1]);
    }

    if (pool.length < DISPLAY_LIMIT) {
      merge(await fetchRandomCatalogProducts(POOL_LIMIT));
    }

    return pool.slice(0, POOL_LIMIT);
  }

  async function fetchContextualCartProducts(cartProductIds, limit) {
    if (!cartProductIds.length) {
      return [];
    }

    var url = new URL(RECOMMENDATIONS_ENDPOINT, window.location.origin);
    url.searchParams.set('strategy', 'contextual');
    url.searchParams.set('variant', 'cart');
    url.searchParams.set('cart', cartProductIds.join(','));
    url.searchParams.set('limit', String(limit));

    var zone = getCustomerZone();
    if (zone) {
      url.searchParams.set('zone', zone);
    }

    try {
      var response = await fetch(url.toString(), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) {
        throw new Error('Status ' + response.status);
      }

      var payload = await response.json();
      var recommendations = Array.isArray(payload && payload.recommendations) ? payload.recommendations : [];
      var fallbacks = Array.isArray(payload && payload.fallbacks) ? payload.fallbacks : [];

      return dedupeProducts(
        recommendations
          .map(function (item) {
            return normalizeRecommendationItem(item, 'contextual');
          })
          .concat(
            fallbacks.map(function (item) {
              return normalizeRecommendationItem(item, 'snapshot-fallback');
            }),
          )
          .filter(Boolean),
        getCartContext(getDrawer()),
      ).slice(0, limit);
    } catch (error) {
      console.debug('[Cart Recommendations] Snapshot recommendations unavailable:', error);
      return [];
    }
  }

  async function fetchPersonalizedProducts(limit) {
    var url = new URL(RECOMMENDATIONS_ENDPOINT, window.location.origin);
    url.searchParams.set('strategy', 'personalized-home');
    url.searchParams.set('engine', 'v2');
    url.searchParams.set('limit', String(limit));

    var zone = getCustomerZone();
    if (zone) {
      url.searchParams.set('zone', zone);
    }

    try {
      var response = await fetch(url.toString(), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) {
        throw new Error('Status ' + response.status);
      }

      var payload = await response.json();
      var recommendations = Array.isArray(payload && payload.recommendations) ? payload.recommendations : [];
      var fallbacks = Array.isArray(payload && payload.fallbacks) ? payload.fallbacks : [];

      return recommendations
        .map(function (item) {
          return normalizeRecommendationItem(item, 'personalized');
        })
        .concat(
          fallbacks.map(function (item) {
            return normalizeRecommendationItem(item, 'snapshot-fallback');
          }),
        )
        .filter(Boolean);
    } catch (error) {
      console.debug('[Cart Recommendations] Personalized recommendations unavailable:', error);
      return [];
    }
  }

  async function fetchDemandProducts(limit) {
    try {
      var response = await fetch(TOP_PRODUCTS_ENDPOINT + '&limit=' + encodeURIComponent(String(limit)), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) {
        throw new Error('Status ' + response.status);
      }

      var payload = await response.json();
      var products = Array.isArray(payload && payload.products) ? payload.products : [];
      return products
        .map(function (item) {
          return normalizeRecommendationItem(item, 'demand');
        })
        .filter(Boolean);
    } catch (error) {
      console.debug('[Cart Recommendations] Demand fallback failed:', error);
      return [];
    }
  }

  async function fetchNewestProducts(limit) {
    try {
      var response = await fetch(NEWEST_PRODUCTS_ENDPOINT + '&limit=' + encodeURIComponent(String(limit)), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) {
        throw new Error('Status ' + response.status);
      }

      var payload = await response.json();
      var products = Array.isArray(payload && payload.products) ? payload.products : [];
      return products
        .map(function (item) {
          return normalizeRecommendationItem(item, 'newest');
        })
        .filter(Boolean);
    } catch (error) {
      console.debug('[Cart Recommendations] Newest fallback failed:', error);
      return [];
    }
  }

  function hashString(value) {
    var input = String(value || '');
    var hash = 2166136261;

    for (var i = 0; i < input.length; i += 1) {
      hash ^= input.charCodeAt(i);
      hash = Math.imul(hash, 16777619);
    }

    return hash >>> 0;
  }

  function getCatalogPageSeed() {
    return [getCustomerKey(), getCustomerZone() || 'no-zone', getCartSignature(getDrawer()), Math.floor(Date.now() / CACHE_TTL_MS), 'cart-catalog-random'].join(':');
  }

  function getRandomCatalogPages() {
    var span = CATALOG_RANDOM_PAGE_MAX - CATALOG_RANDOM_PAGE_MIN + 1;
    var base = hashString(getCatalogPageSeed()) || 1;
    var pages = [];
    var offsets = [0, 7, 15, 21];

    for (var index = 0; index < offsets.length && pages.length < CATALOG_RANDOM_ATTEMPTS; index += 1) {
      var page = CATALOG_RANDOM_PAGE_MIN + ((base + offsets[index]) % span);
      if (pages.indexOf(page) === -1) {
        pages.push(page);
      }
    }

    return pages;
  }

  function buildCatalogPageUrl(page, limit) {
    var url = new URL(CATALOG_PRODUCTS_ENDPOINT, window.location.origin);
    url.searchParams.set('limit', String(limit));
    url.searchParams.set('page', String(page));
    return url.toString();
  }

  async function fetchCatalogPageProducts(page, limit) {
    try {
      var response = await fetch(buildCatalogPageUrl(page, limit), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) {
        throw new Error('Status ' + response.status);
      }

      var payload = await response.json();
      var products = Array.isArray(payload && payload.products) ? payload.products : [];
      return products
        .map(function (item) {
          return normalizeRecommendationItem(item, 'catalog-random');
        })
        .filter(Boolean);
    } catch (error) {
      console.debug('[Cart Recommendations] Random catalog fallback failed:', error);
      return [];
    }
  }

  async function fetchRandomCatalogProducts(limit) {
    var pages = getRandomCatalogPages();
    var products = [];

    for (var index = 0; index < pages.length; index += 1) {
      products = products.concat(await fetchCatalogPageProducts(pages[index], CATALOG_RANDOM_PAGE_SIZE)).slice(0, limit);

      if (products.length >= DISPLAY_LIMIT) {
        break;
      }
    }

    return products.slice(0, limit);
  }

  function buildCardElement(product) {
    if (!product || !product.handle) return null;
    var card = document.createElement('div');
    var title = cleanTitle(product.title || product.handle);
    var href = normalizeProductUrl(product.url, product.handle);
    var image = normalizeImageUrl(product.image);
    card.className = 'productgrid--item neu-card mm-cart-rec-card';
    card.setAttribute('data-handle', product.handle);
    card.setAttribute('data-product-handle', product.handle);
    card.setAttribute('data-mm-rec-surface', 'cart');

    if (product && product.productId) {
      card.setAttribute('data-mm-rec-product-id', product.productId);
    }

    if (product && product.reasonSource) {
      card.setAttribute('data-rec-source', product.reasonSource);
    } else if (product && product.source) {
      card.setAttribute('data-rec-source', product.source);
    }

    card.innerHTML =
      '<a class="neu-card__image-wrap" href="' +
      escapeHtml(href) +
      '">' +
      (image ? '<img src="' + escapeHtml(image) + '" alt="' + escapeHtml(title) + '" loading="lazy" width="220" height="220">' : '') +
      '</a>' +
      '<div class="neu-card__info">' +
      '<a class="neu-card__title" href="' +
      escapeHtml(href) +
      '">' +
      escapeHtml(title) +
      '</a>' +
      (product.priceText ? '<div class="neu-card__price">' + escapeHtml(product.priceText) + '</div>' : '') +
      '</div>';

    return card;
  }

  function isProductAvailable(product) {
    return !product || product.available !== false;
  }

  function buildDisplayProducts(pool) {
    var selection = [];
    var seen = Object.create(null);

    function pushGroup(items) {
      for (var index = 0; index < items.length; index += 1) {
        var item = items[index];
        var handle = String((item && item.handle) || '').trim();
        if (!handle || seen[handle] || selection.length >= DISPLAY_LIMIT) continue;
        seen[handle] = true;
        selection.push(item);
      }
    }

    function bySource(source) {
      return (pool || []).filter(function (product) {
        return product && product.source === source && isProductAvailable(product);
      });
    }

    pushGroup(bySource('contextual'));
    pushGroup(bySource('personalized'));
    pushGroup(bySource('snapshot-fallback'));
    pushGroup(bySource('demand'));
    pushGroup(bySource('newest'));
    pushGroup(bySource('catalog-random'));
    pushGroup(
      (pool || []).filter(function (product) {
        return product && isProductAvailable(product) && !seen[product.handle];
      }),
    );

    return selection;
  }

  function hasCardMarkupForDisplay(entry) {
    if (!entry || !Array.isArray(entry.pool)) {
      return false;
    }

    var displayProducts = buildDisplayProducts(entry.pool);
    return displayProducts.length > 0;
  }

  async function warmDisplayCards(entry) {
    return entry;
  }

  async function loadEntry(force) {
    var cacheKey = getCacheKey();

    if (!force) {
      var cached = getCachedEntry();
      if (cached && hasCardMarkupForDisplay(cached)) {
        return cached;
      }
    }

    if (inflightKey === cacheKey && inflightPromise) {
      return inflightPromise;
    }

    inflightKey = cacheKey;
    inflightPromise = (async function () {
      var entry = !force ? getCachedEntry() : null;
      if (!entry) {
        var pool = await fetchRecommendedPool();
        entry = writePersistentCache(pool);
      }

      entry = await warmDisplayCards(entry);
      currentEntry = entry;
      return entry;
    })();

    try {
      return await inflightPromise;
    } finally {
      if (inflightKey === cacheKey) {
        inflightKey = null;
        inflightPromise = null;
      }
    }
  }

  function emitImpression(rail, products) {
    if (!rail || !products || products.length === 0 || typeof CustomEvent !== 'function') {
      return;
    }

    rail.dispatchEvent(
      new CustomEvent('mm:recommendations:impression', {
        bubbles: true,
        detail: {
          variant: 'cart',
          recommendations: products.map(function (product) {
            return {
              productId: product.productId,
              handle: product.handle,
              reason: product.reason,
              reasonSource: product.reasonSource || product.source || null,
            };
          }),
        },
      }),
    );
  }

  function bindClickAttribution(track) {
    if (!track || track.__mmCartRecsClickBound) return;

    track.__mmCartRecsClickBound = true;
    track.addEventListener('click', function (event) {
      var card = event.target.closest('.mm-cart-rec-card');
      if (!card || typeof CustomEvent !== 'function') return;

      track.dispatchEvent(
        new CustomEvent('mm:recommendations:click', {
          bubbles: true,
          detail: {
            variant: 'cart',
            productId: card.getAttribute('data-mm-rec-product-id'),
            reasonSource: card.getAttribute('data-rec-source'),
          },
        }),
      );
    });
  }

  function hasRenderedCards(track) {
    return !!track && !!track.querySelector('.mm-cart-rec-card:not(.neu-card--skeleton)');
  }

  function getDisplaySignature(products) {
    return (products || [])
      .map(function (product) {
        return String((product && product.handle) || '').trim();
      })
      .filter(Boolean)
      .join('|');
  }

  function notifyCardsReady(track) {
    if (typeof CustomEvent === 'function') {
      track.dispatchEvent(new CustomEvent('mm:cards:added', { bubbles: true }));
      track.dispatchEvent(
        new CustomEvent('theme:rimg:watch', {
          bubbles: true,
          cancelable: true,
        }),
      );
    }

    if (window.rimg && typeof window.rimg.watch === 'function') {
      try {
        window.rimg.watch(track);
      } catch (_) {}
    }

    window.setTimeout(function () {
      var lazyImgs = track.querySelectorAll('img[data-rimg="lazy"]');
      for (var index = 0; index < lazyImgs.length; index += 1) {
        var img = lazyImgs[index];
        if (img.getAttribute('data-rimg') === 'lazy' && img.src) {
          img.setAttribute('srcset', '');
          img.removeAttribute('srcset');
          img.setAttribute('data-rimg', 'loaded');
        }
      }
    }, 220);
  }

  async function renderCurrentDisplay(rail, track) {
    if (!currentEntry || !Array.isArray(currentEntry.pool) || currentEntry.pool.length === 0) {
      if (!hasRenderedCards(track)) {
        setRailVisibility(rail, false);
        track.innerHTML = '';
      }
      return;
    }

    var displayProducts = buildDisplayProducts(currentEntry.pool);
    if (displayProducts.length === 0) {
      if (!hasRenderedCards(track)) {
        setRailVisibility(rail, false);
        track.innerHTML = '';
      }
      return;
    }

    var displaySignature = getDisplaySignature(displayProducts);
    if (displaySignature && track.getAttribute('data-mm-cart-recs-signature') === displaySignature && hasRenderedCards(track)) {
      setRailVisibility(rail, true);
      bindClickAttribution(track);
      return;
    }

    track.innerHTML = '';

    var rendered = 0;
    for (var i = 0; i < displayProducts.length; i += 1) {
      var product = displayProducts[i];
      var card = buildCardElement(product);
      if (!card) continue;
      track.appendChild(card);
      rendered += 1;
    }

    if (rendered === 0) {
      if (!hasRenderedCards(track)) {
        setRailVisibility(rail, false);
        track.innerHTML = '';
      }
      return;
    }

    setRailVisibility(rail, true);
    track.setAttribute('data-mm-cart-recs-signature', displaySignature);
    bindClickAttribution(track);
    emitImpression(rail, displayProducts);
    notifyCardsReady(track);
  }

  async function hydrate(force) {
    var drawer = getDrawer();
    var rail = getRail();
    var track = getRailTrack();
    if (!drawer || !rail || !track) return;

    if (!isDrawerOpen(drawer) || !hasCartItems(drawer)) {
      setRailVisibility(rail, false);
      track.innerHTML = '';
      track.removeAttribute('data-mm-cart-recs-signature');
      return;
    }

    var hasExistingCards = hasRenderedCards(track);
    var cachedEntry = !force ? getCachedEntry({ allowExpired: true }) : null;
    if (cachedEntry && hasCardMarkupForDisplay(cachedEntry)) {
      currentEntry = cachedEntry;
      await renderCurrentDisplay(rail, track);
      if (!cachedEntry.isStale) {
        return;
      }
      force = true;
    }

    if (!hasExistingCards && !hasRenderedCards(track)) {
      track.innerHTML = buildSkeleton(SKELETON_COUNT);
      track.removeAttribute('data-mm-cart-recs-signature');
      setRailVisibility(rail, true);
    } else {
      setRailVisibility(rail, true);
    }

    var tokenAtStart = ++lifecycleToken;
    var entry = await loadEntry(force);

    if (tokenAtStart !== lifecycleToken) {
      return;
    }

    currentEntry = entry;
    await renderCurrentDisplay(rail, track);
  }

  async function prewarm(force) {
    var drawer = getDrawer();
    if (!drawer || !hasCartItems(drawer)) {
      return;
    }

    try {
      await loadEntry(force);
    } catch (_) {}
  }

  function schedulePrewarm(force) {
    window.clearTimeout(prewarmTimer);
    prewarmTimer = window.setTimeout(function () {
      void prewarm(force);
    }, PREWARM_DELAY_MS);
  }

  function scheduleHydrate(force) {
    var drawer = getDrawer();
    if (!isDrawerOpen(drawer)) return;

    window.clearTimeout(hydrateTimer);
    hydrateTimer = window.setTimeout(function () {
      void hydrate(force);
    }, HYDRATE_DELAY_MS);
  }

  function handleCartMutation(force) {
    window.clearTimeout(cartMutationTimer);
    cartMutationTimer = window.setTimeout(function () {
      var drawer = getDrawer();
      var nextCartSignature = getCartSignature(drawer);
      var cartChanged = nextCartSignature !== lastCartSignature;

      lastCartSignature = nextCartSignature;

      if (cartChanged || force) {
        lifecycleToken += 1;
        schedulePrewarm(force || cartChanged);
      }

      scheduleHydrate(force || cartChanged);
    }, CART_MUTATION_DEBOUNCE_MS);
  }

  function handleRecommendationContextChange() {
    clearPersistentCache();
    lifecycleToken += 1;

    if (isDrawerOpen(getDrawer())) {
      scheduleHydrate(true);
    }
  }

  function observeDrawer() {
    var drawer = getDrawer();
    if (!drawer || drawer.__mmCartRecsObserved) return;
    drawer.__mmCartRecsObserved = true;

    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.type !== 'attributes' || mutation.attributeName !== 'aria-hidden') {
          return;
        }

        if (isDrawerOpen(drawer)) {
          schedulePrewarm(false);
          scheduleHydrate(false);
        } else {
          lifecycleToken += 1;
        }
      });
    });

    observer.observe(drawer, {
      attributes: true,
      attributeFilter: ['aria-hidden'],
    });
  }

  function closestCartIntentTarget(target) {
    if (!target || typeof target.closest !== 'function') return null;

    for (var index = 0; index < CART_INTENT_SELECTORS.length; index += 1) {
      var match = target.closest(CART_INTENT_SELECTORS[index]);
      if (match) return match;
    }

    return null;
  }

  function bindCartIntentPrewarm() {
    if (document.__mmCartRecsIntentBound) return;
    document.__mmCartRecsIntentBound = true;

    var handleIntent = function (event) {
      if (!closestCartIntentTarget(event.target)) return;
      schedulePrewarm(false);
    };

    document.addEventListener('mouseover', handleIntent, true);
    document.addEventListener('focusin', handleIntent, true);
    document.addEventListener('touchstart', handleIntent, {
      passive: true,
      capture: true,
    });
    document.addEventListener('click', handleIntent, true);
  }

  function bindGlobalEvents() {
    document.addEventListener('cart:update', function () {
      handleCartMutation(false);
    });
    document.addEventListener('cart:updated', function () {
      handleCartMutation(false);
    });
    document.addEventListener('product-form:cart:update', function () {
      handleCartMutation(false);
    });
    document.addEventListener('mini-cart:refreshed', function () {
      handleCartMutation(false);
    });

    window.addEventListener(
      'garden:auth-ready',
      function () {
        handleRecommendationContextChange();
      },
      { passive: true },
    );

    window.addEventListener(
      'garden:profile-updated',
      function () {
        handleRecommendationContextChange();
      },
      { passive: true },
    );

    window.addEventListener(
      'garden:zone-updated',
      function () {
        handleRecommendationContextChange();
      },
      { passive: true },
    );

    window.addEventListener('storage', function (event) {
      if (event.key === 'usdaZone' || event.key === 'mrmaple_zone' || event.key === 'mrmaple:customer:zone') {
        handleRecommendationContextChange();
      }
    });
  }

  function init() {
    if (!getRail() || !getRailTrack()) return;

    observeDrawer();
    bindGlobalEvents();
    bindCartIntentPrewarm();
    lastCartSignature = getCartSignature(getDrawer());

    if (isDrawerOpen(getDrawer())) {
      scheduleHydrate(false);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
